<template>
  <v-dialog
    :model-value="modelValue"
    fullscreen
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="xl" class="user-dialog-card kb-edit-dialog-card">
      <div class="dialog-header d-flex align-center justify-space-between ga-3">
        <div class="d-flex align-center ga-3 min-w-0">
          <div class="dialog-header__icon">
            <v-icon color="primary" size="22">mdi-book-open-page-variant</v-icon>
          </div>
          <span class="dialog-header__title">Update knowledge base</span>
        </div>
        <div class="d-flex align-center ga-2 flex-shrink-0">
          <v-btn-toggle
            v-model="knowledgeBaseMode"
            mandatory
            divided
            color="primary"
            density="compact"
            class="kb-mode-toggle kb-mode-toggle--header"
            :disabled="saving"
            @update:model-value="onKnowledgeBaseModeChange"
          >
            <v-btn value="manual" size="small" class="text-none">Manual</v-btn>
            <v-btn value="upload" size="small" class="text-none">Upload</v-btn>
          </v-btn-toggle>
          <v-btn
            icon
            variant="text"
            size="small"
            :disabled="saving"
            rounded="lg"
            @click="close"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="dialog-body org-edit-dialog-body">
        <v-form
          ref="formRef"
          v-model="isFormValid"
          class="org-edit-form org-edit-form--knowledge"
          @submit.prevent="submit"
          @keydown.enter="onFormEnterKeydown"
        >
          <KnowledgeBaseModeFields
            dark
            hide-toggle
            v-model:mode="knowledgeBaseMode"
            v-model:text="form.org_knowledeg_base"
            v-model:file="knowledgeBaseFile"
            :manual-rules="manualKnowledgeBaseRules"
            :upload-rules="combinedUploadKnowledgeBaseRules"
            v-model:existing-s3-keys="existingKnowledgeBaseS3Keys"
            v-model:replace-s3-keys="knowledgeBaseReplaceS3Keys"
            :org-id="form.org_id"
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-footer kb-edit-dialog__footer">
        <v-spacer />
        <v-btn
          variant="text"
          class="text-none dialog-btn dialog-btn--muted"
          :disabled="saving"
          rounded="lg"
          @click="close"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="text-none btn-primary-cta"
          :loading="saving"
          :disabled="!isFormValid"
          rounded="lg"
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import updateOrganization, {
  buildUpdateOrganizationInput,
} from '@/mixins/Organization/updateOrganization'
import OrganizationCountryForm from '@/mixins/Organization/OrganizationCountryForm'
import UploadKnowledgeBase, {
  normalizeKnowledgeBaseFiles,
  assignSequentialKnowledgeBaseNames,
} from '@/mixins/Organization/UploadKnowledgeBase'
import KnowledgeBaseModeFields from '@/components/Organizations/KnowledgeBaseModeFields.vue'
import sessionGraphqlMixin from '@/mixins/Organization/sessionGraphqlMixin'
import { isApiSuccess } from '@/mixins/Extras/ApiResponse'
import {
  isKnowledgeBaseS3Key,
  filterKnowledgeBasePreviewKeys,
  getKnowledgeBaseDisplayFilename,
} from '@/mixins/Organization/OrganizationStorage.js'
import {
  listKnowledgeBaseS3Keys,
  removeKnowledgeBaseS3File,
  resolveKnowledgeBaseS3Prefix,
} from '@/utils/knowledgeBaseS3.js'

export default {
  name: 'UpdateKnowledgeBaseDialog',
  components: { KnowledgeBaseModeFields },
  mixins: [
    updateOrganization,
    OrganizationCountryForm,
    UploadKnowledgeBase,
    sessionGraphqlMixin,
  ],
  props: {
    modelValue: { type: Boolean, default: false },
    organization: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved', 'error'],
  data() {
    return {
      form: {
        org_id: '',
        org_name: '',
        org_knowledeg_base: '',
      },
      isFormValid: false,
      saving: false,
      knowledgeBaseMode: 'manual',
      knowledgeBaseFile: null,
      existingKnowledgeBaseS3Keys: [],
      initialKnowledgeBaseS3Keys: [],
      knowledgeBaseReplaceS3Keys: {},
      initialManualText: '',
    }
  },
  computed: {
    ...mapGetters(['sessionContext']),
    hasManualKnowledgeBase() {
      return (this.form.org_knowledeg_base?.trim().length ?? 0) > 0
    },
    hasKnowledgeBaseFiles() {
      return (
        normalizeKnowledgeBaseFiles(this.knowledgeBaseFile).length > 0 ||
        this.existingKnowledgeBaseS3Keys.length > 0
      )
    },
    manualKnowledgeBaseRules() {
      return [
        (v) => {
          const trimmed = v?.trim() || ''
          if (!trimmed && !this.hasKnowledgeBaseFiles) {
            return 'Add manual text or upload at least one file'
          }
          return true
        },
      ]
    },
    combinedUploadKnowledgeBaseRules() {
      return [
        (v) => {
          const files = normalizeKnowledgeBaseFiles(v)
          if (!files.length) {
            if (this.hasManualKnowledgeBase || this.existingKnowledgeBaseS3Keys.length) {
              return true
            }
            return 'Add manual text or upload at least one file'
          }
          for (const file of files) {
            const name = file.name?.toLowerCase() || ''
            if (!name.endsWith('.txt') && !name.endsWith('.pdf')) {
              return 'Only .txt and .pdf files are allowed'
            }
          }
          return true
        },
      ]
    },
  },
  watch: {
    modelValue(open) {
      if (!open || !this.organization) return
      void this.populateFormFromOrganization()
    },
  },
  methods: {
    async populateFormFromOrganization() {
      const org = this.organization

      const listedKeys = await listKnowledgeBaseS3Keys(org)
      const knowledgeBase = org.org_knowledeg_base || ''
      const manualText = isKnowledgeBaseS3Key(knowledgeBase) ? '' : knowledgeBase
      const existingS3Keys = filterKnowledgeBasePreviewKeys(listedKeys)

      this.form = {
        org_id: org.org_id || '',
        org_name: org.org_name || '',
        org_knowledeg_base: manualText,
      }
      this.initialManualText = manualText.trim()

      this.existingKnowledgeBaseS3Keys = existingS3Keys
      this.initialKnowledgeBaseS3Keys = [...existingS3Keys]
      this.knowledgeBaseFile = null
      this.knowledgeBaseReplaceS3Keys = {}
      this.knowledgeBaseMode = existingS3Keys.length > 0 ? 'upload' : 'manual'

      this.$nextTick(() => {
        this.$refs.formRef?.resetValidation()
      })
    },
    onKnowledgeBaseModeChange() {
      this.$nextTick(() => {
        this.$refs.formRef?.resetValidation()
      })
    },
    onFormEnterKeydown(event) {
      if (event.target?.tagName?.toLowerCase() === 'textarea') return
      event.preventDefault()
    },
    close() {
      this.$emit('update:modelValue', false)
    },
    async submit() {
      const validation = await this.$refs.formRef?.validate()
      if (validation === false || validation?.valid === false) return

      const manualText = this.form.org_knowledeg_base?.trim() || ''
      const selectedFiles = normalizeKnowledgeBaseFiles(this.knowledgeBaseFile)
      const hasExistingFiles = this.existingKnowledgeBaseS3Keys.length > 0

      if (!manualText && !selectedFiles.length && !hasExistingFiles) {
        this.$emit('error', {
          status: 'ERROR',
          message: 'Add manual text or upload at least one file',
        })
        return
      }

      this.saving = true
      try {
        const payload = { ...this.form }

        const removedS3Keys = this.initialKnowledgeBaseS3Keys.filter(
          (key) => !this.existingKnowledgeBaseS3Keys.includes(key),
        )
        const replacedOldS3Keys = Object.values(this.knowledgeBaseReplaceS3Keys).filter(
          Boolean,
        )
        let keysToDelete = [...new Set([...removedS3Keys, ...replacedOldS3Keys])]

        if (replacedOldS3Keys.length) {
          const orgId = String(this.form.org_id || '').trim()
          const labelsToReplace = new Set(
            replacedOldS3Keys.map((key) =>
              getKnowledgeBaseDisplayFilename(key, orgId).toLowerCase(),
            ),
          )

          for (const key of this.initialKnowledgeBaseS3Keys) {
            if (keysToDelete.includes(key)) continue
            const label = getKnowledgeBaseDisplayFilename(key, orgId).toLowerCase()
            if (labelsToReplace.has(label)) {
              keysToDelete.push(key)
            }
          }

          keysToDelete = [...new Set(keysToDelete)]
        }

        for (const s3Key of keysToDelete) {
          try {
            await removeKnowledgeBaseS3File(s3Key)
          } catch (err) {
            console.error('Failed to remove knowledge base file', s3Key, err)
            this.$emit('error', {
              status: 'ERROR',
              message: 'Failed to remove the previous knowledge base file.',
            })
            return
          }
        }

        if (selectedFiles.length) {
          // New files get sequential "Knowledge base-N" names; files that
          // replace an existing one keep their name so the replace flow matches.
          const orgId = String(this.form.org_id || '').trim()
          const existingLabels = this.existingKnowledgeBaseS3Keys.map((key) =>
            getKnowledgeBaseDisplayFilename(key, orgId),
          )
          const replacementNames = new Set(
            Object.keys(this.knowledgeBaseReplaceS3Keys).map((name) =>
              String(name || '').trim().toLowerCase(),
            ),
          )
          const filesToUpload = assignSequentialKnowledgeBaseNames(
            selectedFiles,
            existingLabels,
            replacementNames,
          )

          const uploadResult = await this.uploadKnowledgeBaseFiles(
            this.organization,
            filesToUpload,
            resolveKnowledgeBaseS3Prefix(this.organization),
            Object.keys(this.knowledgeBaseReplaceS3Keys),
          )

          if (!isApiSuccess(uploadResult)) {
            this.$emit('error', uploadResult)
            return
          }
        }

        // Files live in S3 directly — the org profile mutation is only
        // needed when the manual text itself changed.
        if (manualText && manualText !== this.initialManualText) {
          payload.org_knowledeg_base = manualText

          const response = await this.updateOrganization(
            buildUpdateOrganizationInput(payload, this.sessionContext),
          )

          if (!isApiSuccess(response)) {
            this.$emit('error', response)
            return
          }

          this.$emit('saved', response)
          this.close()
          return
        }

        this.$emit('saved', {
          status: 'SUCCESS',
          message: 'Knowledge base updated.',
        })
        this.close()
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.dialog-header__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--y-radius-md);
  background: rgba(var(--v-theme-primary), 0.12);
}

.kb-edit-dialog-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100vh;
}

.kb-edit-dialog-card .dialog-body {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.kb-edit-dialog-card .dialog-footer {
  padding: 14px 24px 20px !important;
  gap: 10px;
}

.kb-edit-dialog-card .btn-primary-cta.v-btn {
  min-width: 120px;
  background: var(--y-btn-primary-gradient) !important;
  color: #ffffff !important;
  box-shadow: var(--y-btn-primary-shadow) !important;
}

.kb-edit-dialog-card .btn-primary-cta.v-btn:hover:not(:disabled) {
  filter: brightness(1.06);
  box-shadow: var(--y-btn-primary-shadow-hover) !important;
}

.org-edit-dialog-body {
  padding: 16px 24px 12px !important;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.org-edit-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 920px;
}

.org-edit-form--knowledge {
  flex: 1;
  min-height: 0;
  max-width: none;
  height: 100%;
}

.kb-mode-toggle--header {
  height: 32px;
  border: 1px solid var(--y-glass-border);
  border-radius: 10px;
  overflow: hidden;
  background: var(--y-glass-surface);
}

.kb-mode-toggle--header :deep(.v-btn) {
  min-width: 72px;
  height: 32px !important;
  font-size: 0.75rem;
  letter-spacing: 0;
  color: var(--y-text-secondary) !important;
  background: transparent !important;
}

.kb-mode-toggle--header :deep(.v-btn--active),
.kb-mode-toggle--header :deep(.v-btn--selected) {
  color: var(--y-text) !important;
  background: rgba(99, 102, 241, 0.4) !important;
}
</style>
